import {
	InspectionTemplate,
	InspectionTemplateColumn,
	InspectionTemplateDraft,
} from "@/shared/models/Inspection";
import { getMomentTz } from "@dexteel/mesf-core";

const moment = getMomentTz();

const effectiveTime = (template: InspectionTemplate) =>
	template.EffectiveFrom ? template.EffectiveFrom.getTime() : 0;

const newestFirst = (a: InspectionTemplate, b: InspectionTemplate) =>
	effectiveTime(b) - effectiveTime(a) || b.Rev - a.Rev;

export const sectionRevisions = (
	templates: InspectionTemplate[],
	sectionId: number | null,
) => {
	const all =
		sectionId === null
			? []
			: templates.filter((template) => template.SectionId === sectionId);

	const history = all.filter((template) => template.IsActive).sort(newestFirst);

	const highest = all.reduce(
		(top, template) => Math.max(top, template.Rev),
		-1,
	);

	const latest = all.find((template) => template.IsLatest) ?? null;

	return {
		all,
		history,
		highest,
		latest,
		nextRev: highest + 1,

		/** The revision a section falls back to once `template` is unwound. */
		previousTo: (template: InspectionTemplate | null) =>
			history.find((other) => other.TemplateId !== template?.TemplateId) ??
			null,

		isTaken: (rev: number | null, templateId: number | null) =>
			rev !== null &&
			all.some(
				(template) =>
					template.Rev === rev && template.TemplateId !== templateId,
			),

		isBehind: (rev: number | null) => rev !== null && rev < highest,

		startsBeforeLatest: (effectiveFrom: Date | null) =>
			effectiveFrom !== null &&
			latest?.EffectiveFrom != null &&
			effectiveFrom < latest.EffectiveFrom,

		neighbours: (rev: number | null, templateId: number | null) => {
			const others = all.filter(
				(template) => template.TemplateId !== templateId,
			);

			if (rev === null) return { previous: null, next: null };

			return {
				previous:
					others
						.filter((template) => template.Rev < rev)
						.sort((a, b) => b.Rev - a.Rev)[0] ?? null,
				next:
					others
						.filter((template) => template.Rev > rev)
						.sort((a, b) => a.Rev - b.Rev)[0] ?? null,
			};
		},
	};
};

export type SectionRevisions = ReturnType<typeof sectionRevisions>;

export const nextEffectiveFrom = (previous: Date | null, now: Date) => {
	const today = moment(now).startOf("day").toDate();

	return previous !== null && previous > today ? previous : today;
};

export const columnsOfTemplate = (
	columns: InspectionTemplateColumn[],
	template: InspectionTemplate,
) => columns.filter((column) => column.TemplateId === template.TemplateId);

export const asNewColumns = (columns: InspectionTemplateColumn[]) =>
	columns.map((column) => ({
		...column,
		ColumnId: null,
		TemplateId: null,
	}));

const draftShape = (draft: InspectionTemplateDraft) =>
	JSON.stringify({
		SectionId: draft.SectionId,
		Rev: draft.Rev,
		EffectiveFrom: draft.EffectiveFrom
			? new Date(draft.EffectiveFrom).getTime()
			: null,
		UploadFileId: draft.UploadFileId,
		Columns: draft.Columns.map((column) => [
			column.GroupName,
			column.ColumnLabel.trim(),
			column.ValueTypeCode,
		]),
	});

export const draftHasChanged = (
	draft: InspectionTemplateDraft,
	original: InspectionTemplateDraft,
) => draftShape(draft) !== draftShape(original);

export const asDraft = (
	template: InspectionTemplate,
	columns: InspectionTemplateColumn[],
): InspectionTemplateDraft => ({
	TemplateId: template.TemplateId,
	SectionId: template.SectionId,
	Rev: template.Rev,
	EffectiveFrom: template.EffectiveFrom,
	UploadFileId: template.UploadFileId,
	Columns: columnsOfTemplate(columns, template),
});

export const asNewRevisionOf = (
	draft: InspectionTemplateDraft,
	rev: number,
	latestEffectiveFrom: Date | null,
	now: Date,
): InspectionTemplateDraft => ({
	...draft,
	TemplateId: null,
	Rev: rev,
	EffectiveFrom: nextEffectiveFrom(latestEffectiveFrom, now),
	Columns: asNewColumns(draft.Columns),
});

export const asRevisionDraft = (
	templates: InspectionTemplate[],
	columns: InspectionTemplateColumn[],
	template: InspectionTemplate,
	now: Date,
): InspectionTemplateDraft => {
	const revisions = sectionRevisions(templates, template.SectionId);

	return asNewRevisionOf(
		asDraft(template, columns),
		revisions.nextRev,
		revisions.latest?.EffectiveFrom ?? null,
		now,
	);
};

export const asFirstRevisionDraft = (
	sectionId: number,
	now: Date,
): InspectionTemplateDraft => ({
	TemplateId: null,
	SectionId: sectionId,
	Rev: 0,
	EffectiveFrom: nextEffectiveFrom(null, now),
	UploadFileId: null,
	Columns: [],
});

export const asDuplicateDraft = (
	columns: InspectionTemplateColumn[],
	template: InspectionTemplate,
): InspectionTemplateDraft => ({
	TemplateId: null,
	SectionId: null,
	Rev: null,
	EffectiveFrom: template.EffectiveFrom,
	UploadFileId: template.UploadFileId,
	Columns: asNewColumns(columnsOfTemplate(columns, template)),
});
